import { type DeepReadonly, defineComponent, onBeforeMount } from "vue";
import { createStore, type Store } from "@/common/storeUtils";
import { ContentCard, getInitialSuspendTranslationFns, type TranslationFnsAsync } from "@/components/cards/ContentCard";
import { GaSpinner } from "@/components/general/GaSpinner";
import { getRecordedExpertVideos } from "@newgenerated/shared/stubs";
import { assert } from "@utils/assertion";
import type { ContentMetadata, ContentMetadata_EXPERT_VIDEO, PageInfo } from "@newgenerated/shared/schema";
import { GaButton } from "@/components/general/GaButton";
import { useGaContext } from "@utils/vue-migration/common/gaContext/gaContext";
import type { GaVueComponent } from "@/common/vueUtils";

type LoadingState = {
  kind: "LOADING";
};

type ErrorState = {
  kind: "ERROR";
};

type SoftFetchState = {
  kind: "SOFTFETCH";
  recordedWebinarsNative: ContentMetadata_EXPERT_VIDEO[];
  recordedWebinarsEnglish: ContentMetadata_EXPERT_VIDEO[];
};

type LoadedState = {
  kind: "LOADED";
  recordedWebinarsNative: ContentMetadata_EXPERT_VIDEO[];
  recordedWebinarsEnglish: ContentMetadata_EXPERT_VIDEO[];
};

type WebinarPageState = (LoadedState | LoadingState | SoftFetchState | ErrorState) & {
  translationFnAsync: TranslationFnsAsync;
  pagingNative: PageInfo;
  pagingEnglish: PageInfo;
};

async function fetchRecordedWebinars(store: Store<WebinarPageState>, softFetch?: boolean): Promise<void> {
  const unpackedStore = store.unpackUnion();
  if (softFetch === true && unpackedStore.kind === "LOADED") {
    store.set({
      ...unpackedStore.store.get(),
      kind: "SOFTFETCH",
    });
  } else {
    store.set({
      ...store.get(),
      kind: "LOADING",
    });
  }
  try {
    const result = await getRecordedExpertVideos({
      body: {
        pageNative: store.get().pagingNative,
        pageEnglish: store.get().pagingEnglish,
      },
    });
    assert(result.kind === "success");
    store.set({
      ...store.get(),
      kind: "LOADED",
      recordedWebinarsNative: result.data.expertVideosNative,
      recordedWebinarsEnglish: result.data.expertVideosEnglish,
      pagingNative: result.data.pagingNative,
      pagingEnglish: result.data.pagingEnglish,
    });
  } catch (_) {
    store.set({
      ...unpackedStore.store.get(),
      kind: "ERROR",
    });
  }
}

async function loadMore(store: Store<WebinarPageState>, paging: "pagingNative" | "pagingEnglish"): Promise<void> {
  store
    .sub(paging)
    .sub("page")
    .set(store.get()[paging].page + 1);
  await fetchRecordedWebinars(store, true);
}

function trimMarkdown(text: string): string {
  if (text === "") return "";
  return text
    .replace(/#{1,6}\s/g, "")
    .replace(/[*_]{1,3}(.*?)[*_]{1,3}/g, "$1")
    .replace(/\[([^\]]+)]\([^)]+\)/g, "$1")
    .replace(/^[-*+]\s+/gm, "")
    .trim();
}

function ResultGrid(props: {
  title: string;
  items: DeepReadonly<ContentMetadata[]>;
  paging: DeepReadonly<PageInfo>;
  loadMore: () => void;
  kind: "LOADING" | "LOADED" | "SOFTFETCH" | "ERROR";
  translationFns: Store<TranslationFnsAsync>;
}): GaVueComponent {
  const { t } = useGaContext();
  return (
    <>
      <h2 class="mb-4">{props.title}</h2>
      <div class="ga-dynamic-grid" style="--ga-dynamic-grid--min-width: 15rem;">
        {props.items.length > 0 ? (
          <>
            {props.items.slice(0, props.paging.pageSize).map((expertVideo) => {
              const cleanExpertVideo = {
                ...expertVideo,
                common: {
                  ...expertVideo.common,
                  description: trimMarkdown(expertVideo.common.description ?? ""),
                },
              };
              return <ContentCard content={cleanExpertVideo} layout="vertical" translationFn={props.translationFns.sub(expertVideo.common.language)} />;
            })}
          </>
        ) : (
          <h3>{t("expertVideos:overview.noResults") ?? "expertVideos:overview.noResults"}</h3>
        )}
      </div>
      <div class="mb-4 mt-4">
        {props.paging.totalCount > props.paging.pageSize ? (
          <div class="d-flex justify-content-center">
            <GaButton onClick={props.loadMore}>
              {t("expertVideos:overview.paging.loadMore")} {props.kind === "SOFTFETCH" ? <div class="spinner-border spinner-border-sm"></div> : null}
            </GaButton>
          </div>
        ) : null}
      </div>
    </>
  );
}

export const ExpertVideoOverviewPage = defineComponent({
  setup: () => {
    const store = createStore<WebinarPageState>({
      kind: "LOADING",
      translationFnAsync: getInitialSuspendTranslationFns(),
      pagingNative: {
        page: 0,
        pageSize: 0,
        totalCount: 0,
      },
      pagingEnglish: {
        page: 0,
        pageSize: 0,
        totalCount: 0,
      },
    });
    onBeforeMount(async () => {
      await fetchRecordedWebinars(store);
    });
    return () => {
      const { t, currentLanguage } = useGaContext();
      const loadedState = store.unpackUnion();
      switch (loadedState.kind) {
        case "LOADING": {
          return (
            <div style="display: flex; justify-content: center; align-items: center; height: 1000px;">
              <GaSpinner />
            </div>
          );
        }
        case "SOFTFETCH":
        case "LOADED": {
          return (
            <>
              <div class={"my-4"}>
                <h1>{t("expertVideos:overview.title", "expertVideos:overview.title")}</h1>
                <p class={"lead mb-5"}>{t("expertVideos:overview.lead", "expertVideos:overview.lead")}</p>
              </div>
              <ResultGrid
                title={t("expertVideos:overviewPage.subtitle") ?? "expertVideos:overviewPage.subtitle"}
                items={loadedState.store.sub("recordedWebinarsNative").get()}
                paging={loadedState.store.sub("pagingNative").get()}
                loadMore={() => loadMore(store, "pagingNative")}
                kind={loadedState.kind}
                translationFns={loadedState.store.sub("translationFnAsync")}
              />

              {currentLanguage() !== "en" && loadedState.store.get().pagingNative.totalCount <= loadedState.store.get().pagingNative.pageSize ? (
                <ResultGrid
                  title={t("expertVideos:overviewPage.subtitle.notEn") ?? "expertVideos:overviewPage.subtitle.notEn"}
                  items={loadedState.store.sub("recordedWebinarsEnglish").get()}
                  paging={loadedState.store.sub("pagingEnglish").get()}
                  loadMore={() => loadMore(store, "pagingEnglish")}
                  kind={loadedState.kind}
                  translationFns={loadedState.store.sub("translationFnAsync")}
                />
              ) : null}
            </>
          );
        }
        case "ERROR":
          return <h1>Error</h1>;
      }
    };
  },
});
